import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot } from "vue"

import { IContentOverviewFilter } from "@/models/IContentOverviewFilter";
import { IContentOverviewTile } from "@/models/IContentOverviewTile";
import { IPagination } from "@/models/IPagination";
import { ContentOverviewApi } from "@/services/Api";
import { useAsyncSpinner } from "@/services/Spinner";
import { watchDebounced } from "@vueuse/core";
import queryString from "query-string";
import { computed, nextTick, onMounted, ref, watch } from "vue";
interface Props {
  id: number;
  subject: number;
  type: number;
  pagination: IPagination;
  typeFiltersToShow: IContentOverviewFilter[];
  mainHeader: string;
  subHeader: string;
  description: string;
}

const defaultPageSize = 16;
type PushState = {
  subject: number | null;
  type: number | null;
  page: number | null;
  pageSize: number | null;
};

export default /*#__PURE__*/_defineComponent({
  __name: 'ContentOverviewPageWrapper',
  props: {
    id: {},
    subject: {},
    type: {},
    pagination: {},
    typeFiltersToShow: {},
    mainHeader: {},
    subHeader: {},
    description: {}
  },
  setup(__props: any) {

const props = __props;



const contents = ref<IContentOverviewTile[]>([]);
const activeFilterSubjectId = ref<number>(props.subject);
const activeFilterTypeId = ref<number>(props.type);
const showFilterLevel = ref<number>(props.subject == 0 ? 0 : 1);
const mainHeader = ref<string>(props.mainHeader);
const subHeader = ref<string>(props.subHeader);
const description = ref<string>(props.description);
const typeFiltersToShow = ref<Array<IContentOverviewFilter>>(props.typeFiltersToShow);
const hasLoaded = ref<boolean>(false);
const hideInitial = ref(false);
async function onSubjectFilterClick(id: number) {
  activeFilterSubjectId.value = id;
  updateUrl();
}

async function resetFilters() {
  activeFilterSubjectId.value = 0;
  activeFilterTypeId.value = 0;
  updateUrl();
}

async function onTypeFilterClick(id: number) {
  activeFilterTypeId.value = id;
  updateUrl();
}
async function getContent() {
  await useAsyncSpinner(async () => {
    const contentRes = await ContentOverviewApi.getByFilter(
      props.id,
      activeFilterSubjectId.value,
      activeFilterTypeId.value,
      page.value,
      pageSize.value
    );
    hideInitial.value = true;
    contents.value = contentRes?.tiles.sort(_sortContent).reverse();
    paginationInfo.value = contentRes.pagination;
    typeFiltersToShow.value = contentRes.typeFilters;
    mainHeader.value = contentRes.headline;
    description.value = contentRes.description;
  });
}

function _sortContent(x: IContentOverviewTile, y: IContentOverviewTile) {
  const date1 = new Date(x.date);
  const date2 = new Date(y.date);

  return date1.getTime() - date2.getTime();
}
function updateUrl() {
  const params = {
    pageSize: pageSize.value === defaultPageSize ? null : pageSize.value,
    page: page.value > 1 ? page.value : null,
    subject: activeFilterSubjectId.value === 0 ? null : activeFilterSubjectId.value,
    type: activeFilterTypeId.value === 0 ? null : activeFilterTypeId.value,
  } satisfies PushState;
  const value = queryString.stringify(params, {
    skipNull: true,
  });
  let url = new URL(location.href);
  url.search = value;
  window.history.pushState(structuredClone(params), '', url);
}
const goToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};
const focusAnchor = ref<HTMLElement | null>(null);
onMounted(() => {
  focusAnchor.value = document.getElementById('content-listings-focus-anchor');
});

const query = queryString.parse(location.search);
const paginationInfo = ref<IPagination>(props.pagination);
const totalPages = computed(() => paginationInfo.value.totalPages || 0);
const pageSizeOptions = computed(() => paginationInfo.value.pageSizeOptions || [16]);
const page = ref(query.page ? parseInt(query.page.toString(), 10) : 1);
const pageSize = ref(query.pageSize ? parseInt(query.pageSize.toString(), 10) : defaultPageSize);

watch([activeFilterTypeId, activeFilterSubjectId, pageSize], () => {
  page.value = 1;
});
watchDebounced(
  [activeFilterSubjectId, activeFilterTypeId, page, pageSize],
  async () => {
    goToTop();
    await getContent();
    if (activeFilterSubjectId.value !== 0) {
      showFilterLevel.value = 1;
    } else {
      showFilterLevel.value = 0;
    }
    await nextTick();
    focusAnchor.value?.focus({ preventScroll: true });
  },
  { debounce: 100 }
);
async function pageChange(newPage: number): Promise<void> {
  page.value = newPage;
  updateUrl();
}

async function pageSizeChange(newPageSize: number): Promise<void> {
  pageSize.value = newPageSize;
  updateUrl();
}
window.addEventListener('popstate', async (event: PopStateEvent) => {
  const request = event.state as PushState | null;
  pageSize.value = request?.pageSize ?? defaultPageSize;
  activeFilterTypeId.value = request?.type ?? 0;
  activeFilterSubjectId.value = request?.subject ?? 0;
  page.value = request?.page ?? 1;
});

return (_ctx: any,_cache: any) => {
  return _renderSlot(_ctx.$slots, "default", {
    activeFilterSubjectId: activeFilterSubjectId.value,
    activeFilterTypeId: activeFilterTypeId.value,
    contents: contents.value,
    hideInitial: hideInitial.value,
    onSubjectFilterClick: onSubjectFilterClick,
    onMoveToTopClick: goToTop,
    onTypeFilterClick: onTypeFilterClick,
    showFilterLevel: showFilterLevel.value,
    typeFiltersToShow: typeFiltersToShow.value,
    pagination: _ctx.pagination,
    mainHeader: mainHeader.value,
    subHeader: subHeader.value,
    description: description.value,
    hasLoaded: hasLoaded.value,
    resetFilters: resetFilters,
    currentPage: page.value,
    currentPageSize: pageSize.value,
    totalPages: totalPages.value,
    pageChange: pageChange,
    pageSizeChange: pageSizeChange,
    pageSizeOptions: pageSizeOptions.value
  })
}
}

})